import { useJobStore } from '@stores/job.store'
import { useUserStore } from '@stores/user.store'

import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  startTransition,
  useContext,
  useEffect,
  useState,
} from 'react'

import type {
  ChatMessage,
  ConfirmationDialog,
  EditProfile,
  FullScreenElement,
  InitChat,
  RestResponse,
  User,
} from '@types'

import { useShallow } from 'zustand/react/shallow'

import useHttpRequest from '@hooks/useHttpRequest'
import useIsLoggedIn from '@hooks/user/useIsLoggedIn'
import { chatCVCreationCompletedEvent } from '@lib/analytics-events'
import { asyncNoop, noop, removeObjectKeys, sendGTMEvent } from '@lib/helpers/client'

export type ChatContextType = {
  apply: (jobIds: string[], setApplied: (val: boolean) => void, eventData: Record<string, string | boolean>) => void
  jobIds: string[]
  eventData: Record<string, string | boolean>
  setJobIds: Dispatch<SetStateAction<string[]>>
  isEditProfile: boolean
  setApplied: (val: boolean) => void
  deleteMessage: { index: number; type: string } | undefined
  setDeleteMessage: Dispatch<SetStateAction<{ index: number; type: string } | undefined>>
  editMessage: (message: ChatMessage, jobId?: string) => Promise<boolean>
  questionIndex: number
  setQuestionIndex: Dispatch<SetStateAction<number>>
  updateProfile: boolean
  setUpdateProfile: Dispatch<SetStateAction<boolean>>
  closeChat: boolean
  setCloseChat: Dispatch<SetStateAction<boolean>>
  chat: ChatMessage[]
  setChat: Dispatch<SetStateAction<ChatMessage[]>>
  jobId: string | undefined
  setJobId: Dispatch<SetStateAction<string | undefined>>
  autoReply(
    jobSeeker: User,
    latestMessage?: ChatMessage | undefined,
    jobId?: string | undefined,
    edit?: boolean,
    index?: number,
    customQuestion?: boolean,
  ): Promise<ChatMessage[]>
  submit: () => Promise<void>
  showProgressBar: { show: boolean; progress: number; removeAfter: string } | undefined
  setShowProgressBar: Dispatch<SetStateAction<{ show: boolean; progress: number; removeAfter: string } | undefined>>
  sendMessage: (
    jobSeeker: User,
    message: ChatMessage,
    options?: {
      reInit?: boolean
      jobId?: string
      updateUI?: boolean
      edit?: boolean
      index?: number
      customQuestion?: boolean
    },
  ) => Promise<ChatMessage[] | undefined>
  initFeedbackLoop(jobId: string, setApplied: Dispatch<SetStateAction<boolean>>): void
  initChat: InitChat | undefined
  setInitChat: Dispatch<SetStateAction<InitChat | undefined>>
  showConfirmationDialog: ConfirmationDialog | undefined
  setShowConfirmationDialog: Dispatch<SetStateAction<ConfirmationDialog | undefined>>
  callEditProfile: EditProfile | undefined
  setCallEditProfile: Dispatch<SetStateAction<EditProfile | undefined>>
  editProfile(
    state: string,
    index?: number,
    callback?: (param?: Blob) => Promise<void>,
  ): Promise<ChatMessage | undefined>
  removeFullScreen: boolean
  setRemoveFullScreen: Dispatch<SetStateAction<boolean>>
  fullScreenElement: FullScreenElement | undefined
  setFullScreenElement: Dispatch<SetStateAction<FullScreenElement | undefined>>
  progress: number
}

const ChatContext = createContext<ChatContextType>({
  apply: noop,
  jobIds: [],
  eventData: {},
  setJobIds: noop,
  isEditProfile: false,
  setApplied: noop,
  deleteMessage: undefined,
  setDeleteMessage: noop,
  editMessage: async () => false,
  questionIndex: 1,
  setQuestionIndex: noop,
  updateProfile: false,
  setUpdateProfile: noop,
  closeChat: false,
  setCloseChat: noop,
  chat: [],
  setChat: noop,
  jobId: undefined,
  setJobId: noop,
  autoReply: async () => [],
  submit: asyncNoop,
  showProgressBar: undefined,
  setShowProgressBar: noop,
  sendMessage: async () => undefined,
  initFeedbackLoop: noop,
  initChat: undefined,
  setInitChat: noop,
  showConfirmationDialog: undefined,
  setShowConfirmationDialog: noop,
  callEditProfile: undefined,
  setCallEditProfile: noop,
  editProfile: async () => undefined,
  removeFullScreen: false,
  setRemoveFullScreen: noop,
  fullScreenElement: undefined,
  setFullScreenElement: noop,
  progress: 0,
})

export function ChatContextProvider({ children }: { children: ReactNode }): JSX.Element {
  const { isLoggedIn } = useIsLoggedIn()
  const [profile, setProfile] = useUserStore(useShallow((state) => [state.profile, state.setProfile]))
  const job = useJobStore((st) => st.job)
  const { get, post, put } = useHttpRequest()

  // States
  const [isEditProfile, setIsEditProfile] = useState<boolean>(false)
  const [eventData, setEventData] = useState<Record<string, string | boolean>>({})
  const [jobIds, setJobIds] = useState<string[]>([])
  const [jobId, setJobId] = useState<string | undefined>(undefined)
  const [setApplied, setSetApplied] = useState<(val: boolean) => void>(() => noop)
  const [deleteMessage, setDeleteMessage] = useState<{ index: number; type: string } | undefined>(undefined)
  const [updateProfile, setUpdateProfile] = useState<boolean>(false)
  const [questionIndex, setQuestionIndex] = useState<number>(1)
  const [closeChat, setCloseChat] = useState<boolean>(false)
  const [chat, setChat] = useState<ChatMessage[]>([])
  const [progress, setProgress] = useState<number>(chat[chat.length - 1]?.progress ?? 0)
  // Used for progress message component
  const [showProgressBar, setShowProgressBar] = useState<
    { show: boolean; progress: number; removeAfter: string } | undefined
  >(undefined)
  const [initChat, setInitChat] = useState<InitChat | undefined>(undefined)
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<ConfirmationDialog | undefined>(undefined)
  const [callEditProfile, setCallEditProfile] = useState<EditProfile | undefined>(undefined)
  const [removeFullScreen, setRemoveFullScreen] = useState<boolean>(false)
  const [fullScreenElement, setFullScreenElement] = useState<FullScreenElement | undefined>(undefined)

  function apply(
    JobIds: string[],
    SetApplied: (val: boolean) => void,
    EventData: Record<string, string | boolean>,
  ): void {
    setIsEditProfile(false)
    setEventData(EventData)
    setJobIds(JobIds)
    setSetApplied(() => SetApplied)
  }

  useEffect(() => {
    startTransition(() => {
      const lastMessage = chat[chat.length - 1]
      if (lastMessage && lastMessage.progress) {
        setProgress(lastMessage.progress)
      }
    })
  }, [chat])

  async function editMessage(message: ChatMessage, currentJobId?: string): Promise<boolean> {
    if (typeof window !== 'undefined' && isLoggedIn) {
      let chatResponse: RestResponse<string | User> | undefined
      if (currentJobId) {
        chatResponse = await post<RestResponse<string>>(`chat/update/${profile?.user?.id}/${currentJobId}`, {
          body: { ...removeObjectKeys(message, 'script') },
        })
      } else if (updateProfile) {
        chatResponse = await post<RestResponse<User>>(`chat/update/${profile?.user?.id}`, {
          body: { ...removeObjectKeys(message, 'script') },
        })
      } else {
        chatResponse = await post<RestResponse<string>>(`chat/edit/${profile?.user?.id}`, {
          body: { ...removeObjectKeys(message, 'script') },
        })
      }
      if (typeof chatResponse?.data === 'string') {
        return chatResponse?.data === 'Chat edited successfully'
      }
      if (chatResponse?.data) {
        setUpdateProfile(false)
        setQuestionIndex(1)
        setProfile({ ...profile, user: chatResponse?.data })
        setCloseChat(true)
        return true
      }
    }
    return false
  }

  const sendMessage = async (
    jobSeeker: User,
    message: ChatMessage,
    _options?: {
      reInit?: boolean
      jobId?: string
      updateUI?: boolean
      edit?: boolean
      index?: number
      customQuestion?: boolean
    },
  ): Promise<ChatMessage[] | undefined> => {
    const options = {
      reInit: false,
      updateUI: true,
      edit: false,
      index: 1,
      jobId: undefined,
      customQuestion: false,
      ..._options,
    }
    if (typeof window !== 'undefined' && jobSeeker.id) {
      let chatResponse: RestResponse<ChatMessage[]> | undefined
      if (options.customQuestion) {
        const internalJobIds = options.jobId === jobIds[0] ? jobIds : [...jobIds, options.jobId]
        const jobs = new Set(internalJobIds)
        jobs.add(job?.id || '')
        chatResponse = await post<RestResponse<ChatMessage[]>>(
          `chat/send/multiple/${jobSeeker.id}/?edit=${options.edit}&index=${options.index}`,
          {
            body: {
              component: { ...removeObjectKeys(message, 'script') },
              ids: Array.from(jobs),
            },
          },
        )
      } else {
        chatResponse = await post<RestResponse<ChatMessage[]>>(
          `chat/send/${jobSeeker.id}?jobId=${eventData.jobId}&edit=${options.edit}&index=${options.index}`,
          {
            body: { ...removeObjectKeys(message, 'script') },
          },
        )
      }
      if (options.reInit) {
        setChat([])
        const chatData = await get<RestResponse<ChatMessage[]>>(`chat/initiate/${jobSeeker.id}?origin=WEB`)
        const newChat = chatData?.data
        if (newChat) {
          setChat(newChat)
        }
      } else {
        if (chat.length > 0) {
          chat[chat.length - 1] = message
          chat[chat.length - 1].answered = true
        }
        if (options.updateUI) {
          setChat(
            (previousChat: ChatMessage[]) => previousChat.concat(chatResponse?.data || []) || chatResponse?.data || [],
          )
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      await autoReply(
        jobSeeker,
        chatResponse?.data[chatResponse.data.length - 1],
        jobId || options.jobId,
        options.edit,
        undefined,
        options.customQuestion,
      )
      return chatResponse?.data
    }
    return undefined
  }

  async function autoReply(
    jobSeeker: User,
    latestMessage?: ChatMessage,
    currentJobId?: string,
    edit = false,
    index = 1,
    customQuestion = false,
  ): Promise<ChatMessage[]> {
    if (latestMessage) {
      if (latestMessage && ['TEXT'].includes(latestMessage.type)) {
        await sendMessage(jobSeeker, latestMessage, { jobId: currentJobId, edit, index, customQuestion })
      } else if (latestMessage && latestMessage.type === 'PREVIEW') {
        const previewResponse = await put<RestResponse<User>>(`chat/preview/${jobSeeker.id}`)
        if (previewResponse?.data) {
          const submitResponse = await put<RestResponse<User>>(`chat/preview/submit/${jobSeeker.id}`)
          if (submitResponse?.data) {
            sendGTMEvent('cv_completed')
            chatCVCreationCompletedEvent('Created', currentJobId)
            setProfile({ ...jobSeeker, user: submitResponse.data })
          }
        }
      }
      return [latestMessage]
    }
    return []
  }

  function initFeedbackLoop(currentJobId: string, setAppliedCallback: Dispatch<SetStateAction<boolean>>): void {
    setSetApplied(() => setAppliedCallback)
    setInitChat({
      popupData: {
        count: 0,
        type: 'START_CUSTOM_QUESTION',
      },
      jobIds: [currentJobId],
      index: 1,
    })
  }

  async function editProfile(
    state: string,
    index = 1,
    callback: ((param?: Blob) => Promise<void>) | undefined = undefined,
  ): Promise<ChatMessage | undefined> {
    setIsEditProfile(true)
    setRemoveFullScreen(true)
    setInitChat({
      popupData: {
        type: state,
        count: 1,
      },
      jobIds: [],
      callback: (param: Blob) => {
        setCloseChat(true)
        if (callback) {
          callback(param)
        }
      },
      index,
    })

    if (state === 'CHANGE_PROFILE_PICTURE') {
      return undefined
    }

    // TODO: Refactor initializeChat() function in order to remove double backend request
    let query: string
    if (['ASK_ADDITIONAL_QUESTION', 'START_CUSTOM_QUESTION'].includes(state)) {
      query = `chat/initiate/${profile?.user?.id}?origin=WEB&state=${state}`
    } else {
      query =
        state === 'PROFILE' ? `chat/initiate/${profile?.user?.id}?origin=WEB` : `chat/initiate/custom?state=${state}`
    }

    const newChat = await get<RestResponse<ChatMessage[]>>(query)

    if (newChat?.data) {
      return newChat?.data[newChat.data.length - 1]
    }

    return undefined
  }

  async function submit(): Promise<void> {
    const submitResponse = await put<RestResponse<User>>(`chat/submit/${profile?.user?.id}`)

    if (submitResponse?.data) {
      sendGTMEvent('cv_completed')
      chatCVCreationCompletedEvent('Uploaded', jobId)
      setProfile({ ...profile, user: submitResponse.data })
    }
    if (jobIds.length === 0) {
      if (!profile.user?.isGuestUser) {
        setCloseChat(true)
        setChat([])
      }
    }
  }

  const sharedState: ChatContextType = {
    apply,
    jobIds,
    eventData,
    setJobIds,
    isEditProfile,
    setApplied,
    deleteMessage,
    setDeleteMessage,
    editMessage,
    questionIndex,
    setQuestionIndex,
    updateProfile,
    setUpdateProfile,
    closeChat,
    setCloseChat,
    chat,
    setChat,
    jobId,
    setJobId,
    autoReply,
    submit,
    showProgressBar,
    setShowProgressBar,
    sendMessage,
    initFeedbackLoop,
    initChat,
    setInitChat,
    showConfirmationDialog,
    setShowConfirmationDialog,
    callEditProfile,
    setCallEditProfile,
    editProfile,
    removeFullScreen,
    setRemoveFullScreen,
    fullScreenElement,
    setFullScreenElement,
    progress,
  }

  return <ChatContext.Provider value={sharedState}>{children}</ChatContext.Provider>
}

export function useChatContext(): ChatContextType {
  return useContext<ChatContextType>(ChatContext)
}
